import React, { useState, useMemo, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";

import {
    Grid,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Typography,
    FormControl,
    FormControlLabel,
    Switch,
    FormHelperText,
    Select,
    MenuItem,
    Chip,
    Autocomplete,
    CircularProgress,
    GlobalStyles,
    useTheme,
    useMediaQuery,
    Tooltip,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { frFR, enUS } from "@mui/x-data-grid/locales";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Add, Close, CloudUpload, Schedule, Publish } from "@mui/icons-material";
import {
    fetchAlbums,
    createAlbum,
    fetchAlbumById,
    updateAlbum,
    deleteAlbum,
    scheduleAlbum,
    publishAlbumRequest
} from "../../Redux/Reducers/AlbumsReducer";
import { fetchCategoryTree } from "../../Redux/Reducers/CategoryTreeReducer";
import { fetchPodcaster } from "../../Redux/Reducers/PodcasterReducer";
import { CustomLoadingOverlay } from "../../components/common/TableLoader";
import { useTranslation } from "react-i18next";
import { formatUTCDate, formatUTCDateForDataGrid } from "../../utils/dateUtils";
import ScheduleRequestModal from "./partials/ScheduleRequestModal";
import { formatEnumLabel } from "../../utils/formatEnumLabel";
import SelectField from "../../components/common/SelectField";
import usePermissions from "../../hooks/usePermissions";
import { Image } from "react-bootstrap";
import { getFullUrl } from "../../utils/globalUtils";
import { t } from "i18next";

const TYPE_OPTIONS = [
    { label: t('albums.audiobook', "Audiobook"), value: "AUDIOBOOK" },
    { label: t('albums.podcast', "Podcast"), value: "PODCAST" }
];

const flattenCategories = (categories, parent = "") => {
    if (!Array.isArray(categories)) return [];

    let result = [];
    categories.forEach((cat) => {
        const name =
            cat.translations?.find((t) => t.lang === "en")?.name || cat.name;

        const path = parent ? `${parent} > ${name}` : name;

        if (cat.children && cat.children.length > 0) {
            result = [...result, ...flattenCategories(cat.children, path)];
        } else {
            result.push({
                id: cat.id,
                label: name,
                group: parent || "Other",
                fullPath: path,
            });
        }
    });

    return result;
};

const EMPTY_FORM = {
    contentLanguage: "en",
    title: "",
    description: "",
    type: "",
    categoryIds: [],
    podcasterId: "",
    coverImage: null,
    status: "DRAFT",
    metaKeywords: "",
    isExplicit: false
};

const MODULE_KEY = "album";

function AlbumManagement() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { hasAction } = usePermissions();

    const { albums = [], loading, pagination } = useSelector((state) => state.albums || {});
    const { categories = [] } = useSelector((state) => state.categoryTree);
    const { podcasters = [] } = useSelector(
        (state) => state.podcasters
    );


    const { user } = useSelector((state) => state.auth || state.AuthReducer || {});
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitAction, setSubmitAction] = useState(null);
    const [scheduling, setScheduling] = useState(false);
    const [fetchingDetails, setFetchingDetails] = useState(false);

    const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
    const [scheduleSelectedRecord, setScheduleSelectedRecord] = useState(null);

    const [publishAlbumDialogOpen, setPublishAlbumDialogOpen] = useState(false);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const {
        control,
        handleSubmit,
        // watch,
        reset,
        setValue,
        formState: { errors }
    } = useForm({
        defaultValues: EMPTY_FORM
    });

    // const selectedType = watch("type");

    // For Table Thead Width
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
    const isLarge = useMediaQuery(theme.breakpoints.up('xl'));

    const [togglingId, setTogglingId] = useState(null);

    // Fetch albums on mount
    useEffect(() => {
        dispatch(
            fetchAlbums({
                page: paginationModel.page + 1,
                limit: paginationModel.pageSize,
                search: searchText,
                ...((user?.roleName === "Podcaster" || user?.roleId === 3) ? { podcasterId: user?._id || user?.id } : {})
            })
        );
    }, [paginationModel, searchText, dispatch, user]);

    // Fetch categories when type changes
    useEffect(() => {
        // if (selectedType) {
        //     dispatch(fetchCategoryTree({ fetchType: "dropdown", type: selectedType, limit: 1000 }));
        // }
        dispatch(fetchCategoryTree({ fetchType: "dropdown", limit: 1000 }));

        dispatch(fetchPodcaster({ fetchType: "dropdown" }));
    }, [dispatch]);

    // Get categories for selected type
    const availableCategories = useMemo(() => {
        return Array.isArray(categories) ? categories : [];
    }, [categories]);

    const categoryOptions = useMemo(() => {
        return flattenCategories(availableCategories);
    }, [availableCategories]);

    const handleOpen = async (rowId = null) => {
        if (rowId) {
            setEditing({ id: rowId });
            setModalOpen(true);
            setFetchingDetails(true);
            try {
                const row = await dispatch(fetchAlbumById(rowId)).unwrap();
                setEditing(row);
                setImagePreview(row.coverImage?.cdnUrl || null);
                reset({
                    contentLanguage: row.contentLanguage || "en",
                    title: row.title || row.titleEn || "",
                    description: row.description || row.descriptionEn || "",
                    type: row.type || "",
                    categoryIds: row.categories?.map(cat => cat.id) || row.categoryIds || [],
                    podcasterId: row.podcasterId || "",
                    coverImage: null,
                    status: row.status || "DRAFT",
                    metaKeywords: row.metaKeywords || "",
                    isExplicit: row.isExplicit || false
                });
            } catch (err) {
                console.error("Failed to fetch album details:", err);
            } finally {
                setFetchingDetails(false);
            }
        } else {
            setEditing(null);
            setImagePreview(null);
            reset({
                ...EMPTY_FORM,
                podcasterId: (user?.roleName === "Podcaster" || user?.roleId === 3) ? (user?._id || user?.id) : ""
            });
            setModalOpen(true);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];

        if (!file) return;

        // IMAGE TYPE VALIDATION
        const allowedTypes = [
            "image/png",
            "image/jpeg",
            "image/jpg",
            "image/webp"
        ];

        if (!allowedTypes.includes(file.type)) {
            toast.error(
                t(
                    'albums.validations.image_allowed',
                    "Only PNG, JPG, JPEG and WEBP images are allowed"
                )
            );

            e.target.value = null; // IMPORTANT
            return;
        }

        // IMAGE SIZE VALIDATION
        if (file.size > 5 * 1024 * 1024) {
            toast.error(
                t(
                    'albums.validations.image_size',
                    "Image must be under 5MB"
                )
            );

            e.target.value = null; // IMPORTANT
            return;
        }

        // REMOVE OLD PREVIEW
        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
        }

        // SET FORM VALUE
        setValue("coverImage", file, {
            shouldValidate: true,
            shouldDirty: true,
        });

        // PREVIEW
        setImagePreview(URL.createObjectURL(file));

        // RESET INPUT
        e.target.value = null;
    };

    useEffect(() => {
        return () => {
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    const handleSubmitForm = async (data) => {
        setSubmitting(true);
        try {
            const formData = new FormData();

            formData.append("title", data.title);
            formData.append("description", data.description || "");
            formData.append("metaKeywords", data.metaKeywords || "");
            formData.append("type", data.type);
            formData.append("status", data.status);
            if (data.status === "PUBLISHED") {
                formData.append("publishAt", new Date().toISOString());
            }
            formData.append("podcasterId", data.podcasterId || "");
            formData.append("isExplicit", data.isExplicit);

            data.categoryIds.forEach(id => {
                formData.append("categoryIds", id);
            });

            if (data.coverImage && data.coverImage instanceof File) {
                formData.append("coverImage", data.coverImage);
            }

            if (editing) {
                await dispatch(updateAlbum({ id: editing.id, formData })).unwrap();
                toast.success(t('albums.messages.update_success', "Album updated successfully"));
            } else {
                await dispatch(createAlbum(formData)).unwrap();
                toast.success(t('albums.messages.create_success', "Album created successfully"));
            }

            setModalOpen(false);
            dispatch(
                fetchAlbums({
                    page: paginationModel.page + 1,
                    limit: paginationModel.pageSize,
                    search: searchText,
                })
            );
        } catch (err) {
            console.error("Album error:", err);
            toast.error(err?.message || t('something_went_wrong', "Something went wrong"));
        } finally {
            setSubmitting(false);
            setSubmitAction(null);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteDialogOpen(true);
    };

    const handlePublishAlbumRequest = (id) => {
        setSelectedId(id);
        setPublishAlbumDialogOpen(true);
    };

    const handlePublishAlbumRequestConfirm = async () => {
        setPublishing(true);
        try {
            const response = await dispatch(publishAlbumRequest({ id: selectedId })).unwrap();
            toast.success(response.message);
            dispatch(
                fetchAlbums({
                    page: paginationModel.page + 1,
                    limit: paginationModel.pageSize,
                    search: searchText,
                })
            );
        } catch (err) {
            toast.error(err);
        } finally {
            setPublishAlbumDialogOpen(false);
            setSelectedId(null);
            setPublishing(false);
        }
    };

    const handleDeleteConfirm = async () => {
        setDeleting(true);
        try {
            await dispatch(deleteAlbum({ id: selectedId })).unwrap();
            toast.success(t('albums.messages.delete_success', "Album deleted successfully"));
            dispatch(
                fetchAlbums({
                    page: paginationModel.page + 1,
                    limit: paginationModel.pageSize,
                    search: searchText,
                    ...((user?.roleName === "Podcaster" || user?.roleId === 3) ? { podcasterId: user?._id || user?.id } : {})
                })
            );
        } catch (err) {
            toast.error(t('albums.messages.delete_failed', "Failed to delete album"));
        } finally {
            setDeleteDialogOpen(false);
            setSelectedId(null);
            setDeleting(false);
        }
    };

    const handleExplicitToggle = async (rowId, newStatus) => {
        setTogglingId(rowId);
        try {
            const row = await dispatch(fetchAlbumById(rowId)).unwrap();
            const formData = new FormData();
            formData.append("title", row.title || row.titleEn || "");
            formData.append("description", row.description || row.descriptionEn || "");
            formData.append("metaKeywords", row.metaKeywords || "");
            formData.append("type", row.type || "");
            formData.append("status", row.status || "DRAFT");
            formData.append("podcasterId", row.podcasterId || "");
            formData.append("isExplicit", newStatus);

            const categoryIds = row.categories?.map(cat => cat.id) || row.categoryIds || [];
            categoryIds.forEach(id => {
                formData.append("categoryIds", id);
            });

            await dispatch(updateAlbum({ id: rowId, formData })).unwrap();
            dispatch(
                fetchAlbums({
                    page: paginationModel.page + 1,
                    limit: paginationModel.pageSize,
                    search: searchText,
                    ...((user?.roleName === "Podcaster" || user?.roleId === 3) ? { podcasterId: user?._id || user?.id } : {})
                })
            );
            toast.success(t('albums.messages.explicit_status_updated', "Explicit status updated successfully"));
        } catch (error) {
            toast.error(error?.message || t('something_went_wrong', "Something went wrong"));
        } finally {
            setTogglingId(null);
        }
    };

    const columns = [
        {
            field: 'sno',
            headerName: t('sno', 'S.No'),
            width: 50,
            sortable: false,
            renderCell: (params) => {
                // const rowsInCurrentPage = params.api.getRowsCount();
                const allRows = params.api.getAllRowIds();
                const currentRowIndex = allRows.indexOf(params.id);
                const visibleRowIndex = currentRowIndex % paginationModel.pageSize;
                return paginationModel.page * paginationModel.pageSize + visibleRowIndex + 1;
            }
        },
        {
            field: "coverImage",
            headerName: "",
            width: 60,
            sortable: false,
            renderCell: (params) => (
                <Image src={params.value} style={{ width: 32, height: 32, borderRadius: '50%' }} />
            ),
        },
        {
            field: "title",
            headerName: t('albums.fields.title', "Title"),
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {params.row.isExplicit && (
                        <Tooltip title={t("albums.fields.explicit", "Explicit Content")} arrow placement="top">
                            <Box sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                color: 'rgba(255, 255, 255, 0.7)',
                                borderRadius: '3px',
                                padding: '0 4px',
                                minWidth: '16px',
                                height: '16px',
                                fontSize: '10px',
                                fontWeight: '700',
                                mr: 1,
                                cursor: 'default'
                            }}>
                                E
                            </Box>
                        </Tooltip>
                    )}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={params.value}>{params.value}</span>
                </Box>
            )
        },
        {
            field: "type",
            headerName: t('albums.fields.type', "Type"),
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
        },
        {
            field: "categoryNames",
            headerName: t('albums.fields.categories', "Categories"),
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
            renderCell: (params) => {
                const categories = params.row.categories || [];
                if (categories.length === 0) return "N/A";

                return (
                    <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {categories.slice(0, 2).map((cat, index) => (
                            <Chip
                                key={index}
                                label={cat.name}
                                size="small"
                                sx={{
                                    backgroundColor: 'rgba(230, 210, 249, 0.2)',
                                    color: '#fff',
                                    fontSize: '12px',
                                }}
                            />
                        ))}
                        {categories.length > 2 && (
                            <Chip
                                label={`+${categories.length - 2}`}
                                size="small"
                                sx={{
                                    backgroundColor: 'rgba(230, 210, 249, 0.2)',
                                    color: '#fff',
                                    fontSize: '12px',
                                }}
                            />
                        )}
                    </Box>
                );
            }
        },
        {
            field: "createdAt",
            headerName: t('albums.fields.createdAt', "Created At"),
            ...(isMobile && { width: 200 }),
            ...(isLarge && { flex: 1 }),
            valueFormatter: (value) => {
                return formatUTCDateForDataGrid(value);
            }
        },
        {
            field: "status",
            headerName: t('albums.fields.status', "Status"),
            ...(isMobile && { width: 120 }),
            ...(isLarge && { flex: 1 }),
            renderCell: (params) => {
                const isScheduled = params.row.originalStatus === "SCHEDULED";
                const scheduledAt = params.row.scheduledAt;

                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '100%' }} title={params.value}>
                        <Typography sx={{ color: params.value === "Published" ? "#03c11c" : "#e3b220", fontWeight: 'bold', fontSize: '13px', lineHeight: 1.3 }}>
                            {params.value}
                        </Typography>
                        {isScheduled && scheduledAt && (
                            <Typography sx={{ color: '#bdbdbd', fontSize: '11px', lineHeight: 1.3, mt: 0.25 }}>
                                ({formatUTCDateForDataGrid(scheduledAt)})
                            </Typography>
                        )}
                    </Box>
                );
            }
        },
        {
            field: "actions",
            headerName: t('actions', "Actions"),
            minWidth: 220,
            ...(isMobile && { width: 220 }),
            ...(isLarge && { flex: 1 }),
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: '6px', flexWrap: 'nowrap' }}>
                    {hasAction(MODULE_KEY, 'read') && (
                        <IconButton
                            size="small"
                            component={Link}
                            to={`/albums/${params?.row?.slug}`}
                            sx={{
                                backgroundColor: "rgba(227,214,245,0.2) !important",
                                width: "36px",
                                height: "36px",
                                border: "1px solid rgba(227,214,245,0.1)",
                                padding: "0"
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="13" viewBox="0 0 18 13" fill="none">
                                <path d="M17.415 4.66071C15.5893 1.74214 12.4457 0 9 0C5.55429 0 2.41071 1.74214 0.585 4.66071L0.437143 4.89857C0.154286 5.36143 0 5.88857 0 6.42857C0 6.96857 0.154286 7.49571 0.437143 7.95857L0.585 8.19643C2.41071 11.115 5.55429 12.8571 9 12.8571C12.4457 12.8571 15.5893 11.115 17.415 8.19643L17.5629 7.95857C17.8457 7.49571 18 6.96857 18 6.42857C18 5.88857 17.8457 5.36143 17.5629 4.89857L17.415 4.66071ZM16.47 7.27714L16.3221 7.515C14.7343 10.0543 11.9957 11.5714 9 11.5714C6.00429 11.5714 3.26571 10.0543 1.67786 7.515L1.53 7.27714C1.36929 7.02 1.28571 6.73071 1.28571 6.42857C1.28571 6.12643 1.36929 5.83714 1.53 5.58L1.67786 5.34214C3.26571 2.80286 6.00429 1.28571 9 1.28571C11.9957 1.28571 14.7343 2.80286 16.3221 5.34214L16.47 5.58C16.6307 5.83714 16.7143 6.12643 16.7143 6.42857C16.7143 6.73071 16.6307 7.02 16.47 7.27714Z" fill="#ffffff" />
                                <path d="M8.99972 2.57129C6.87186 2.57129 5.14258 4.30057 5.14258 6.42843C5.14258 8.55629 6.87186 10.2856 8.99972 10.2856C11.1276 10.2856 12.8569 8.55629 12.8569 6.42843C12.8569 4.30057 11.1276 2.57129 8.99972 2.57129ZM8.99972 8.99986C7.57901 8.99986 6.42829 7.84915 6.42829 6.42843C6.42829 5.00772 7.57901 3.857 8.99972 3.857C10.4204 3.857 11.5711 5.00772 11.5711 6.42843C11.5711 7.84915 10.4204 8.99986 8.99972 8.99986Z" fill="#ffffff" />
                            </svg>
                        </IconButton>
                    )}

                    {hasAction(MODULE_KEY, 'update') && (
                        <IconButton
                            size="small"
                            component={Link}
                            onClick={() => handleOpen(params.row.id)}
                            sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M15.531 3.84226C13.9473 5.42594 10.2302 9.14231 9.3237 10.0459C9.12779 10.2411 8.89463 10.3952 8.64435 10.4958C7.91732 10.7975 6.14371 11.4932 5.62228 11.5283C5.31165 11.5491 5.00699 11.4344 4.7865 11.2139C4.566 10.9934 4.45129 10.6887 4.47215 10.3781C4.50716 9.85668 5.2029 8.08453 5.49937 7.36048C5.60291 7.10348 5.7571 6.87032 5.95301 6.67516L12.1581 0.469295C12.4582 0.169095 12.8665 0 13.291 0C13.7156 0 14.1239 0.169095 14.424 0.469295L15.531 1.57623C15.8312 1.87643 16.0003 2.28465 16.0003 2.70925C16.0003 3.13384 15.8312 3.54206 15.531 3.84226ZM5.59174 10.4087C6.10125 10.3446 7.59031 9.72334 8.21603 9.46336C8.21826 9.46262 8.2205 9.46187 8.22199 9.46113C8.33894 9.4142 8.44471 9.34418 8.53336 9.25553L8.5341 9.25479C9.44214 8.35121 13.1577 4.63559 14.7406 3.0519C14.8315 2.96103 14.8829 2.83811 14.8829 2.70925C14.8829 2.58038 14.8315 2.45746 14.7406 2.36659L13.6337 1.25965C13.5428 1.16877 13.4199 1.11737 13.291 1.11737C13.1622 1.11737 13.0393 1.16877 12.9484 1.25965L6.74261 7.46551C6.65322 7.55415 6.5832 7.66068 6.53627 7.77763L6.53478 7.78061C6.27779 8.40782 5.6558 9.89839 5.59174 10.4087Z" fill="white" />
                                <path fillRule="evenodd" clipRule="evenodd" d="M14.0542 4.52797C14.2717 4.74623 14.2717 5.10006 14.0542 5.31832C13.8359 5.53658 13.4821 5.53658 13.2638 5.31832L10.6813 2.73571C10.463 2.51745 10.463 2.16362 10.6813 1.94536C10.8995 1.72785 11.2534 1.72785 11.4716 1.94536L14.0542 4.52797Z" fill="white" />
                                <path fillRule="evenodd" clipRule="evenodd" d="M9.49485 9.08712C9.71311 9.30464 9.71311 9.65922 9.49485 9.87673C9.27734 10.095 8.92277 10.095 8.70525 9.87673L6.12268 7.29412C5.90442 7.07661 5.90442 6.72203 6.12268 6.50451C6.34019 6.28626 6.69476 6.28626 6.91227 6.50451L9.49485 9.08712Z" fill="white" />
                                <path fillRule="evenodd" clipRule="evenodd" d="M5.40054 1.47412C5.70893 1.47412 5.95922 1.72441 5.95922 2.03281C5.95922 2.3412 5.70893 2.59149 5.40054 2.59149H2.79339C1.86747 2.59149 1.11735 3.34162 1.11735 4.26754V13.2065C1.11735 13.6512 1.2939 14.0773 1.60824 14.3916C1.92259 14.706 2.34868 14.8825 2.79339 14.8825H12.4771C12.9218 14.8825 13.3479 14.706 13.6623 14.3916C13.9766 14.0773 14.1532 13.6512 14.1532 13.2065V10.5993C14.1532 10.2909 14.4034 10.0406 14.7118 10.0406C15.0202 10.0406 15.2705 10.2909 15.2705 10.5993V13.2065C15.2705 13.9477 14.9763 14.6576 14.4526 15.182C13.9282 15.7057 13.2183 15.9999 12.4771 15.9999H2.79339C2.05221 15.9999 1.34231 15.7057 0.817903 15.182C0.294237 14.6576 0 13.9477 0 13.2065V4.26754C0 2.72483 1.25069 1.47412 2.79339 1.47412H5.40054Z" fill="white" />
                            </svg>
                        </IconButton>
                    )}

                    {hasAction(MODULE_KEY, 'delete') && (
                        <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(params.row.id)}
                            sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
                                <path d="M7.21951 1.17073C6.4558 1.17073 5.80457 1.65856 5.56322 2.34141C5.45548 2.64622 5.12105 2.80599 4.81623 2.69825C4.51142 2.59051 4.35166 2.25608 4.4594 1.95127C4.86091 0.81529 5.94427 0 7.21951 0C8.49483 0 9.57815 0.81529 9.97971 1.95127C10.0874 2.25608 9.92765 2.59051 9.62287 2.69825C9.31801 2.80599 8.98357 2.64622 8.87586 2.34141C8.63454 1.65856 7.98322 1.17073 7.21951 1.17073Z" fill="white" />
                                <path d="M0 3.70744C0 3.38415 0.26208 3.12207 0.585366 3.12207H13.8537C14.177 3.12207 14.4391 3.38415 14.4391 3.70744C14.4391 4.03072 14.177 4.2928 13.8537 4.2928H0.585366C0.26208 4.2928 0 4.03072 0 3.70744Z" fill="white" />
                                <path d="M2.47023 5.61969C2.44873 5.29712 2.1698 5.05306 1.84723 5.07456C1.52466 5.09606 1.28059 5.37499 1.3021 5.69757L1.66382 11.1233C1.73054 12.1245 1.78444 12.9332 1.91086 13.5678C2.04229 14.2275 2.26584 14.7786 2.72758 15.2106C3.1893 15.6426 3.75403 15.8289 4.42107 15.9162C5.06266 16.0001 5.87318 16.0001 6.87657 16.0001H7.56246C8.56586 16.0001 9.37632 16.0001 10.018 15.9162C10.685 15.8289 11.2497 15.6426 11.7115 15.2106C12.1732 14.7786 12.3967 14.2275 12.5282 13.5678C12.6545 12.9332 12.7085 12.1245 12.7752 11.1233L13.1369 5.69757C13.1584 5.37499 12.9143 5.09606 12.5918 5.07456C12.2692 5.05306 11.9902 5.29712 11.9688 5.61969L11.6098 11.0043C11.5397 12.0564 11.4896 12.7883 11.38 13.339C11.2735 13.8733 11.125 14.156 10.9116 14.3557C10.6982 14.5553 10.4062 14.6847 9.86607 14.7554C9.30927 14.8282 8.57562 14.8294 7.52133 14.8294H6.9177C5.86342 14.8294 5.12972 14.8282 4.57292 14.7554C4.03282 14.6847 3.7408 14.5553 3.5274 14.3557C3.314 14.156 3.16545 13.8733 3.05903 13.339C2.94932 12.7883 2.89935 12.0564 2.82922 11.0043L2.47023 5.61969Z" fill="white" />
                                <path d="M5.20978 7.02736C5.53147 6.9952 5.81828 7.2299 5.85051 7.55161L6.24076 11.4541C6.27291 11.7757 6.03822 12.0626 5.71651 12.0948C5.39484 12.1269 5.10797 11.8922 5.07581 11.5705L4.68557 7.66806C4.65339 7.34642 4.8881 7.05952 5.20978 7.02736Z" fill="white" />
                                <path d="M9.75346 7.66806C9.78561 7.34642 9.55092 7.05952 9.22921 7.02736C8.90757 6.9952 8.62066 7.2299 8.5885 7.55161L8.19826 11.4541C8.1661 11.7757 8.4008 12.0626 8.72251 12.0948C9.04415 12.1269 9.33098 11.8922 9.36321 11.5705L9.75346 7.66806Z" fill="white" />
                            </svg>
                        </IconButton>
                    )}

                    {/* publish requests */}
                    {(params.row?.originalStatus !== "PUBLISHED" && (user?.roleName === "Podcaster" || user?.roleId === 3 || user?.roleName === "Super Admin" || user?.roleId === 1) && (hasAction(MODULE_KEY, 'publishRequests'))) && (
                        <IconButton
                            size="small"
                            title={t("albums.actions.publish_request", "Publish Request")}
                            onClick={() => handlePublishAlbumRequest(params.row.id)}
                            sx={{ color: '#ffffff', backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
                        >
                            <Publish fontSize="small" />
                        </IconButton>
                    )}

                    {/* schedule requests */}
                    {(params.row?.originalStatus !== "PUBLISHED" && params.row?.originalStatus !== "SCHEDULED" && (user?.roleName === "Podcaster" || user?.roleId === 3 || user?.roleName === "Super Admin" || user?.roleId === 1) && (hasAction(MODULE_KEY, 'publishRequests'))) && (
                        <IconButton
                            size="small"
                            title={t('albums.actions.schedule_album_request', "Schedule Album Request")}
                            onClick={() => {
                                setScheduleSelectedRecord(params.row);
                                setScheduleModalOpen(true);
                            }}
                            sx={{ color: '#ffffff', backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
                        >
                            <Schedule fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            )
        }
    ];

    const rows = useMemo(() => {
        if (!Array.isArray(albums) || albums.length === 0) {
            return [];
        }

        return albums.map((album, index) => {
            if (!album || !album.id) {
                console.warn('Invalid album at index', index, album);
                return null;
            }

            return {
                id: album.id,
                backendId: album.id,
                slug: album.slug,
                sno: index + 1,
                title: album.title || "",
                status: formatEnumLabel(album.status),
                originalStatus: album.status,
                type: album.type || "",
                categoryId: album.categoryId || "",
                categoryName: album.categoryName || null,
                categories: album.categories || [],
                podcasterId: album.podcasterId || "",
                podcaster: album.podcaster || null,
                coverImage: getFullUrl(album.coverImage?.cdnUrl, 'album'),
                totalDuration: album.totalDuration || 0,
                createdAt: album.createdAt || null,
                scheduledAt: album.publishAt || null,
                isExplicit: album.isExplicit || false,
            };
        }).filter(Boolean);
    }, [albums]);

    // if (loading) {
    //     return (
    //         <Box p={4} display="flex" justifyContent="center">
    //             <CircularProgress />
    //         </Box>
    //     );
    // }

    //const [open, setOpen] = useState(true);

    return (
        <Box sx={{ p: '36px 24px 24px' }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: '24px', flexWrap: 'wrap', columnGap: '6px', rowGap: '16px' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px' }} className='main_title'>{t('albums.title', 'Albums')}
                </Typography>

                <Box display="flex" gap={2}>
                    <TextField
                        size="small"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder={t('search', 'Search')}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '999px',
                                backgroundColor: 'rgba(230, 210, 249, 0.2)',
                                color: '#fff',
                                height: '38px',
                                '& fieldset': {
                                    borderColor: 'rgba(230, 210, 249, 0.1)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(230, 210, 249, 0.1)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgba(230, 210, 249, 0.1)',
                                    borderWidth: '1px',
                                },
                            },
                            '& .MuiOutlinedInput-input': {
                                padding: '10px 16px',
                                color: '#FF69B4',
                                fontSize: '15px',
                                lineHeight: '18px',

                            },
                            '& input::placeholder': {
                                color: 'rgba(255,255,255,0.6)',
                                opacity: 1,
                            },
                            '& .MuiFormHelperText-root': {
                                ml: '20px'
                            }
                        }}
                    />
                    <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen(null)}
                        sx={{
                            border: '1px solid rgba(230, 210, 249, 0.1) !important',
                            color: '#fff',
                            textTransform: 'none',
                            backgroundColor: '#FF69B4 !important',
                            borderRadius: '999px',
                            fontSize: '15px',
                            lineHeight: '24px',
                            padding: '0',
                            minWidth: '38px',
                            '& .MuiButton-startIcon': {
                                margin: 0,
                            },
                        }}
                    >
                    </Button>
                </Box>
            </Box>

            {/* Table */}
            <Box sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px', px: '12px' }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    slots={{ toolbar: GridToolbar, loadingOverlay: CustomLoadingOverlay }}
                    slotProps={{
                        toolbar: {
                            csvOptions: { disableToolbarButton: true },
                            printOptions: { disableToolbarButton: true },
                        },
                    }}
                    paginationMode="server"
                    rowCount={pagination?.total || 0}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => {
                        setPaginationModel(model);
                    }}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                        sorting: { sortModel: [{ field: "createdAt", sort: "desc" }] }
                    }}
                    getRowId={(row) => row.id || row.sno}
                    pageSizeOptions={[10, 25, 50, 100]}
                    disableRowSelectionOnClick
                    localeText={
                        i18n.language?.toLowerCase().startsWith("fr")
                            ? frFR.components.MuiDataGrid.defaultProps.localeText
                            : enUS.components.MuiDataGrid.defaultProps.localeText
                    }
                    sx={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: '0',
                        '& .MuiDataGrid-toolbarContainer': {
                            padding: '12px',
                            margin: '0 -12px',
                            gap: '10px',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                        '& .MuiDataGrid-topContainer::after': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
                            color: '#fff',
                            border: '1px solid rgba(230, 210, 249, 0.2)',
                            borderRadius: '999px',
                            textTransform: 'none',
                            fontWeight: 500,
                            padding: '6px 12px',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                        '& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-icon .MuiBadge-root svg': {
                            width: '18px',
                            height: '18px',
                        },
                        '& .MuiDataGrid-virtualScrollerContent': {
                            maxHeight: 'auto',
                            height: 'auto !important'
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            borderRadius: '0 !important',
                        },
                        '& .MuiDataGrid-virtualScrollerRenderZone': {
                            position: 'inherit',
                        },
                        '& .MuiDataGrid-virtualScrollerRenderZone>.MuiDataGrid-row': {
                            maxHeight: 'none !important',
                            height: 'auto !important',
                        },
                        '& .MuiDataGrid-cell': {
                            height: 'auto !important',
                            minHeight: 'var(--height)',
                            paddingTop: '5px',
                            paddingBottom: '5px',
                        },
                        '& .MuiDataGrid-cell>.MuiBox-root': {
                            minHeight: '100%',
                            alignItems: 'center',
                        },
                        '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': {
                            borderRadius: '0 !important',
                            borderColor: 'rgba(230, 210, 249, 0.2) !important',
                            color: '#fff',
                        },
                        '& .MuiDataGrid-columnHeaders [role=row]': {
                            backgroundColor: '#1a1a1a',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderColor: 'rgba(230, 210, 249, 0.2) !important'
                        },
                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader': {
                            color: 'white',
                        },
                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                            opacity: '0.8 !important',
                            fill: 'white',
                        },
                        '& .MuiDataGrid-cell .MuiChip-outlined': {
                            border: '0 !important',
                            height: 'auto',
                            fontWeight: '600',
                        },
                        '& .MuiDataGrid-cell .MuiChip-outlined.MuiChip-colorSuccess': {
                            color: "#03c11c",
                        },
                        '& .MuiTablePagination-root': {
                            color: '#fff'
                        },
                        '& .MuiTablePagination-selectLabel': {
                            marginBottom: '0',
                        },
                        '& .MuiTablePagination-input': {
                            marginRight: '10px',
                        },
                        '& .MuiTablePagination-input .MuiSelect-select': {
                            paddingTop: '2px',
                            paddingBottom: '2px',
                            border: '1px solid rgba(230, 210, 249, 0.2)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            borderRadius: '4px',
                        },
                        '& .MuiTablePagination-input .MuiSvgIcon-root': {
                            fill: 'white'
                        },
                        '& .MuiTablePagination-toolbar .MuiTablePagination-displayedRows': {
                            marginBottom: '0',
                        },
                        '& .MuiTablePagination-actions': {
                            marginLeft: '10px !important',
                        },
                        '& .MuiTablePagination-actions .MuiButtonBase-root': {
                            color: '#FF69B4',
                        },
                        '& .MuiDataGrid-filler': {
                            height: 'auto !important',
                        },
                        '& .MuiDataGrid-filler>div': {
                            borderColor: 'transparent',
                        },
                        '& .MuiDataGrid-overlayWrapper': {
                            height: 'auto !important',
                            maxHeight: '50px',
                        },
                        '& .MuiDataGrid-overlayWrapperInner': {
                            minHeight: '40px',
                            height: 'auto !important'
                        },
                        '& .MuiDataGrid-overlay': {
                            color: '#fff',
                            borderTop: '1px solid rgba(230, 210, 249, 0.2)',
                            backgroundColor: 'transparent',
                            minHeight: '40px',
                        },
                    }}
                />
            </Box>

            {/* Add/Edit Dialog */}
            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="md">
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
                    {editing ? t('albums.popup_titles.edit_album', 'Edit Album') : t('albums.popup_titles.add_album', "Add Album")}
                    <IconButton onClick={() => setModalOpen(false)} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        borderColor: 'rgba(230, 210, 249, 0.1)',
                        padding: '24px 16px',
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(230, 210, 249, 0.3)',
                            borderRadius: '4px',
                            '&:hover': {
                                background: 'rgba(230, 210, 249, 0.5)',
                            },
                        },
                    }}
                >
                    {fetchingDetails ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={4} minHeight={200}>
                            <CircularProgress sx={{ color: '#FF69B4' }} />
                        </Box>
                    ) : (
                        <>
                    {/* ALL FIELDS GRID */}
                    <Grid container columnSpacing={2}>
                        {/* Title */}
                        <Grid item xs={12}>
                            <Controller
                                name="title"
                                control={control}
                                rules={{ required: t('albums.validations.title_required', "Title required") }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        error={!!errors.title}
                                        helperText={errors.title?.message}
                                        placeholder={t('albums.fields.title', "Title")}
                                        sx={{
                                            mb: 2,
                                            '& .MuiOutlinedInput-input': {
                                                padding: '12px 20px',
                                                fontWeight: '400',
                                                color: '#fff',
                                            },
                                            '& input::placeholder': {
                                                color: 'rgba(255,255,255,0.6)',
                                                opacity: 1,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Description */}
                        <Grid item xs={12}>
                            <Controller
                                name="description"
                                control={control}
                                rules={{ required: t('albums.validations.description_required', "Description required") }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        multiline
                                        rows={3}
                                        error={!!errors.description}
                                        helperText={errors.description?.message}
                                        placeholder={t('albums.fields.description', "Description")}
                                        sx={{
                                            mb: 2,
                                            '& .MuiOutlinedInput-input': {
                                                padding: '0 10px',
                                                fontWeight: '400',
                                                color: '#fff',
                                                '&::-webkit-scrollbar': {
                                                    width: '4px',
                                                },
                                                '&::-webkit-scrollbar-track': {
                                                    background: 'rgba(255, 255, 255, 0.05)',
                                                    borderRadius: '4px',
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    background: 'rgba(230, 210, 249, 0.3)',
                                                    borderRadius: '4px',
                                                },
                                            },
                                            '& input::placeholder': {
                                                color: 'rgba(255,255,255,0.6)',
                                                opacity: 1,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Type */}
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="type"
                                control={control}
                                rules={{ required: t('albums.validations.type_required', "Type is required") }}
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.type} sx={{ mb: 2 }}>
                                        <Select
                                            {...field}
                                            displayEmpty
                                            sx={{
                                                color: "#fff",
                                                fontWeight: 400,
                                                "& .MuiSelect-select": {
                                                    padding: "12px 20px",
                                                },
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    border: "1px solid rgba(230, 210, 249, 0.1) !important",
                                                },
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                    border: "1px solid rgba(230, 210, 249, 0.1)",
                                                },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                    border: "1px solid rgba(230, 210, 249, 0.1)",
                                                },
                                                "& .MuiSvgIcon-root": {
                                                    color: "#fff",
                                                },
                                            }}
                                        >
                                            <MenuItem value="" disabled>{t('albums.fields.type_empty', "Select Type")}</MenuItem>
                                            {TYPE_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}

                                        </Select>
                                        <FormHelperText>{errors.type?.message}</FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        {/* Categories - Multiple Selection */}
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="categoryIds"
                                sx={{ mb: 2 }}
                                control={control}
                                rules={{ required: t('albums.validations.category_required', "Please select atleast one Category") }}
                                render={({ field: { onChange, value, ...field } }) => (
                                    <Autocomplete
                                        {...field}
                                        multiple
                                        // open={open}
                                        // onOpen={() => setOpen(true)}
                                        // onClose={() => setOpen(false)}
                                        options={categoryOptions}
                                        getOptionLabel={(option) => option.label}
                                        groupBy={(option) => option.group}
                                        value={categoryOptions.filter(option => value?.includes(option.id))}
                                        onChange={(event, newValue) => {
                                            onChange(newValue.map(item => item.id));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={t('select_categories', "Select Categories")}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        padding: '4px 15px',
                                                        color: '#fff',
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: '1px solid rgba(230, 210, 249, 0.1) !important',
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        border: '1px solid rgba(230, 210, 249, 0.1)',
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        border: '1px solid rgba(230, 210, 249, 0.1)',
                                                    },
                                                    '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
                                                        color: '#fff',
                                                    },
                                                }}
                                            />
                                        )}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => {
                                                const { key, ...tagProps } = getTagProps({ index });
                                                return (
                                                    <Chip
                                                        key={key}
                                                        {...tagProps}
                                                        label={option.label}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: 'rgba(230, 210, 249, 0.2)',
                                                            color: '#fff',
                                                            border: '1px solid rgba(230, 210, 249, 0.1)',
                                                            '& .MuiChip-deleteIcon': {
                                                                color: '#fff',
                                                                '&:hover': {
                                                                    color: '#ff69b4',
                                                                },
                                                            },
                                                        }}
                                                    />
                                                );
                                            })
                                        }
                                        renderOption={(props, option) => {
                                            const { key, ...optionProps } = props;

                                            return (
                                                <li key={key} {...optionProps}>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {option.label}
                                                        </Typography>

                                                        <Typography
                                                            variant="caption"
                                                            sx={{ color: 'rgba(255,255,255,0.5)' }}
                                                        >
                                                            {option.group}
                                                        </Typography>
                                                    </Box>
                                                </li>
                                            );
                                        }}
                                        ListboxProps={{
                                            sx: {
                                                backgroundColor: '#252525',
                                                color: '#fff',
                                                '& .MuiAutocomplete-option': {
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(230, 210, 249, 0.1)',
                                                    },
                                                    '&[aria-selected="true"]': {
                                                        backgroundColor: 'rgba(230, 210, 249, 0.2)',
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                            <FormHelperText
                                sx={{
                                    color: '#d32f2f',
                                    marginLeft: '14px',
                                }}
                            >
                                {errors.categoryIds?.message}
                            </FormHelperText>
                        </Grid>

                        {/* Podcaster */}
                        <Grid item xs={12} md={6}>
                            <SelectField
                                name="podcasterId"
                                control={control}
                                label={t("albums.fields.select_podcaster", "Select Podcaster")}
                                options={podcasters}
                                labelKey="name"
                                rules={{
                                    required: t(
                                        "albums.validations.podcaster_required",
                                        "Please select podcaster"
                                    ),
                                }}
                                disabled={user?.roleName === "Podcaster" || user?.roleId === 3}
                            />
                        </Grid>

                    </Grid>

                    {/* IMAGE FULL WIDTH */}
                    <Controller
                        name="coverImage"
                        control={control}
                        render={() => (
                            <>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: '400', fontSize: '14px' }}>
                                    {t("albums.fields.upload_cover_image", "Upload Cover Image")} <Typography component="span" sx={{ fontSize: '13px', color: '#ff4d4f', ml: 1 }}>{t("albums.fields.upload_only_square_img", "(Upload Only Square Image)")}</Typography>
                                </Typography>
                                <Box
                                    component="label"
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const file = e.dataTransfer.files[0];
                                        if (file) {
                                            handleImageChange({ target: { files: [file] } });
                                        }
                                    }}
                                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                    sx={{
                                        border: "1px dashed rgba(230, 210, 249, 0.2)",
                                        p: 3,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        borderRadius: "16px",
                                    }}
                                >
                                    <input hidden type="file" accept="image/png, image/jpg, image/webp" onChange={handleImageChange} />
                                    {imagePreview ? (
                                        <img src={imagePreview} width={150} alt="preview" />
                                    ) : (
                                        <>
                                            <Box sx={{ textAlign: 'center', pointerEvents: 'none' }}>
                                                <CloudUpload fontSize="large" />
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontSize: '14px',
                                                        my: '3px',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    {t(
                                                        'administrator.fields.upload_image',
                                                        'Upload Image (File must be less than 5MB)'
                                                    )}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '13px', color: '#ff4d4f', textAlign: 'center', mb: '4px' }}>
                                                    {t('administrator.fields.upload_square_only', '(Upload Only Square Image)')}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: '12px',
                                                        color: "rgba(255,255,255,0.6)",
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    {t('administrator.fields.click_browse', "or click to browse (Supported formats: PNG, JPG, WEBP)")}
                                                </Typography>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </>
                        )}
                    />
                    {/* META DATA */}
                    <Box sx={{ mt: 2 }}>
                        {/* <Typography variant="h6" sx={{ mb: '5px', fontSize: '16px', fontWeight: '400' }}>
                            {t("meta_data", "Meta Data")}
                        </Typography> */}
                        <Grid container columnSpacing={2}>
                            {/* <Grid item xs={12} md={6}>
                                <Controller
                                    name="metaTitle"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            placeholder={t("meta_title", "Meta Title")}
                                            sx={{
                                                mb: 2,
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '12px 20px',
                                                    fontWeight: '400',
                                                    color: '#fff',
                                                },
                                                '& input::placeholder': {
                                                    color: 'rgba(255,255,255,0.6)',
                                                    opacity: 1,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Grid> */}
                            <Grid item xs={12} md={6}>
                                {/* <SelectField
                                    name="metaKeywords"
                                    control={control}
                                    label={t("keywords", "Keywords")}
                                    freeSolo={true}
                                    limitTags={20}
                                /> */}
                                <Controller
                                    name="metaKeywords"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            placeholder={t("albums.fields.keywords", "Keywords")}
                                            sx={{
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '12px 20px',
                                                    fontWeight: '400',
                                                    color: '#fff',
                                                },
                                                '& input::placeholder': {
                                                    color: 'rgba(255,255,255,0.6)',
                                                    opacity: 1,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="isExplicit"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={field.value}
                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: '#ee3918',
                                                        },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            backgroundColor: '#ee3918',
                                                        },
                                                    }}
                                                />
                                            }
                                            label={t("albums.fields.isExplicit", "Explicit Content")}
                                            sx={{ color: '#fff', ml: 1, mt: 1 }}
                                        />
                                    )}
                                />
                            </Grid>
                            {/* <Grid item xs={12}>
                                <Controller
                                    name="metaDescription"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            multiline
                                            rows={3}
                                            placeholder={t("meta_description", "Meta Description")}
                                            sx={{
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '0 10px',
                                                    fontWeight: '400',
                                                    color: '#fff',
                                                },
                                                '& input::placeholder': {
                                                    color: 'rgba(255,255,255,0.6)',
                                                    opacity: 1,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Grid> */}
                        </Grid>
                    </Box>
                    <Controller
                        name="status"
                        control={control}
                        render={({ field }) => <input type="hidden" {...field} />}
                    />
                    </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', gap: '6px' }}>
                    <Button onClick={() => setModalOpen(false)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}>
                        {t("cancel", "Cancel")}
                    </Button>
                    <Button
                        disabled={submitting}
                        onClick={() => { setSubmitAction('DRAFT'); setValue('status', 'DRAFT', { shouldDirty: true }); handleSubmit(handleSubmitForm)(); }}
                        sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}>
                        {submitting && submitAction === 'DRAFT' && <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />}{t("albums.albumDetails.actions.save_as_draft", "Save As Draft")}
                    </Button>
                    {((user?.roleName === "Podcaster" || user?.roleId === 3) && !user?.isVerified) ? (
                        <Button
                            variant="outlined"
                            disabled={submitting}
                            onClick={() => { setSubmitAction('PENDING_APPROVAL'); setValue('status', 'PENDING_APPROVAL', { shouldDirty: true }); handleSubmit(handleSubmitForm)(); }}
                            sx={{
                                py: 1.5,
                                border: '1px solid #FF69B4',
                                borderRadius: '999px',
                                fontWeight: '400',
                                fontSize: '15px',
                                lineHeight: '20px',
                                background: 'transparent',
                                color: '#FF69B4',
                                textTransform: 'capitalize',
                                minWidth: { xs: '100%', sm: '150px' },
                                marginLeft: '10px',
                                '&:hover': {
                                    border: '1px solid #FF69B4',
                                }
                            }}
                        >
                            {submitting && submitAction === 'PENDING_APPROVAL' && <CircularProgress size={20} sx={{ color: '#FF69B4', mr: 1 }} />}{t("albums.albumDetails.actions.save_as_request_review", "Save And Request For Review")}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            disabled={submitting}
                            onClick={() => { setSubmitAction('PUBLISHED'); setValue('status', 'PUBLISHED', { shouldDirty: true }); handleSubmit(handleSubmitForm)(); }}
                            sx={{
                                py: 1.5,
                                borderRadius: '999px',
                                fontWeight: '400',
                                fontSize: '15px',
                                lineHeight: '20px',
                                background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                                textTransform: 'capitalize',
                                minWidth: { xs: '100%', sm: '150px' },
                                marginLeft: '10px',
                            }}
                        >
                            {submitting && submitAction === 'PUBLISHED' && <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />}{t("albums.albumDetails.actions.save_and_publish_now", "Save And Publish Now")}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Delete Album  */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center' }}>{t("albums.popup_titles.confirm_delete", "Confirm Delete")}</DialogTitle>
                <DialogContent sx={{ pb: '0' }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center' }}>{t("albums.messages.delete_album_confirmation", "Are you sure you want to delete this album?")}</Typography>
                </DialogContent>
                <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}> {t("cancel", "Cancel")} </Button>
                    <Button
                        variant="contained"
                        disabled={deleting}
                        onClick={handleDeleteConfirm}
                        sx={{
                            py: 1.5,
                            borderRadius: '999px',
                            fontWeight: '400',
                            fontSize: '15px',
                            lineHeight: '20px',
                            background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                            textTransform: 'capitalize',
                            minWidth: { xs: '100%', sm: '150px' },
                            marginLeft: '10px',
                        }}
                    >
                        {deleting ? <CircularProgress size={20} /> : t("delete", "Delete")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Publish Album Request  */}
            <Dialog open={publishAlbumDialogOpen} onClose={() => setPublishAlbumDialogOpen(false)}>
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center' }}>{t("albums.popup_titles.confirm_publish", "Confirm Publish")}</DialogTitle>
                <DialogContent sx={{ pb: '0' }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center' }}>{t("albums.messages.publish_album_confirmation", "Are you sure you want to publish this album?")}</Typography>
                </DialogContent>
                <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
                    <Button onClick={() => setPublishAlbumDialogOpen(false)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}> {t("cancel", "Cancel")} </Button>
                    <Button
                        variant="contained"
                        disabled={publishing}
                        onClick={handlePublishAlbumRequestConfirm}
                        sx={{
                            py: 1.5,
                            borderRadius: '999px',
                            fontWeight: '400',
                            fontSize: '15px',
                            lineHeight: '20px',
                            background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                            textTransform: 'capitalize',
                            minWidth: { xs: '100%', sm: '150px' },
                            marginLeft: '10px',
                        }}
                    >
                        {publishing ? <CircularProgress size={20} /> : t("albums.actions.publish", "Publish")}
                    </Button>
                </DialogActions>
            </Dialog>

            <ScheduleRequestModal
                open={scheduleModalOpen}
                handleClose={() => setScheduleModalOpen(false)}
                selectedRecord={scheduleSelectedRecord}
                loading={scheduling}
                onSubmit={async (data) => {
                    setScheduling(true);
                    try {
                        await dispatch(scheduleAlbum({
                            id: scheduleSelectedRecord.id,
                            payload: {
                                publishAt: data.publishAt,
                                timezone: data.timezone,
                                note: data.note
                            }
                        })).unwrap();

                        // Reload the current table page after the API confirms scheduling.
                        await dispatch(fetchAlbums({
                            page: paginationModel.page + 1,
                            limit: paginationModel.pageSize,
                            search: searchText,
                            ...((user?.roleName === "Podcaster" || user?.roleId === 3)
                                ? { podcasterId: user?._id || user?.id }
                                : {})
                        })).unwrap();
                    } catch (err) {
                        toast.error(err?.message || err || t('something_went_wrong', "Something went wrong"));
                    } finally {
                        setScheduling(false);
                        setScheduleModalOpen(false);
                    }
                }}
            />

            <GlobalStyles
                styles={{
                    '.MuiDataGrid-panel .MuiDataGrid-paper,.MuiPopover-root .MuiPopover-paper, .base-Popper-root .MuiPaper-root, .MuiDialog-root .MuiPaper-root': {
                        backgroundColor: '#252525 !important',
                        border: '1px solid rgba(230, 210, 249, 0.1)',
                        fontSize: '15px',
                        color: '#fff',
                        borderRadius: '4px',
                    },
                    '.MuiDialog-root .MuiPaper-root': {
                        borderRadius: '20px',
                    },
                    '.MuiDataGrid-columnsManagementHeader': {
                        padding: '10px !important',
                    },
                    '.MuiInputBase-formControl': {
                        fontSize: '14px !important',
                        color: 'white !important',
                        borderRadius: '999px !important',
                    },
                    '.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiInputBase-formControl .MuiInputAdornment-positionStart svg': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter': {
                        borderTop: '1px solid rgba(230, 210, 249, 0.1)',
                        padding: '4px 8px 4px 16px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement': {
                        padding: '0px 16px 12px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label,.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root, .MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiButtonBase-root': {
                        color: '#FF69B4',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root,.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root svg,.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiFormLabel-root': {
                        color: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:before': {
                        borderColor: 'rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:after': {
                        display: 'none',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root, .base-Popper-root .MuiPaper-root .MuiList-root': {
                        padding: '0',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root': {
                        padding: '5px 10px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root.Mui-selected .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: '#FF69B4',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiDivider-root': {
                        borderColor: 'rgba(230, 210, 249, 0.3) !important',
                        margin: '0',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        minWidth: '32px',
                    },
                    '.MuiDataGrid-scrollbarFiller': {
                        borderTop: 'none !important',
                    },
                    '& .MuiDataGrid-scrollbar': {
                        '&::-webkit-scrollbar': {
                            width: '8px',
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(230, 210, 249, 0.3)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: 'rgba(230, 210, 249, 0.5)',
                        },
                    },
                    '& .MuiListSubheader-root': {
                        backgroundColor: '#5f5965 !important',
                        color: '#fff !important'
                    }
                }}
            />
        </Box>
    );
}

export default AlbumManagement;
